import {
  translationService,
  TranslationsService,
} from "../i18n/TranslationsService";
import {
  globalSearchService,
  GlobalSearchService,
} from "../services/GlobalSearchService";
import {
  SearchFilter,
  SearchFilterOptions,
} from "../search/components/SearchFilter";
import { ArticleListingCard } from "../listings/components/ArticleListingCard";
import { DateRangeSearchFilter } from "../search/components/DateRangeSearchFilter";
import { DateRange } from "../models/DateRange";
import { searchContext, SearchContext } from "../contexts/SearchContext";
import { translate } from "../i18n/translate";
import languageService from "../services/LanguageService";

export class ArticleListingPage {
  private service: GlobalSearchService;
  private translationService: TranslationsService;
  private searchContext: SearchContext;
  private dateRangeFilter: DateRangeSearchFilter | null = null;
  private searchFilter: SearchFilter | null = null;

  // Pre-existing, static placeholders in markup
  private dateRangeFilterSlot: HTMLDivElement | null = null;
  private taxonomyFilterSlot: HTMLDivElement | null = null;

  private apifilterMapping: Record<
    string,
    { filter: string; label: string; placeholder: string }
  > = {
    taxonomy: {
      filter: "categories",
      label: translate("Hero.Category"),
      placeholder: translate("Hero.FindCategory"),
    },
  };

  constructor(
    service: GlobalSearchService,
    translationService: TranslationsService,
    searchContext: SearchContext
  ) {
    this.service = service;
    this.translationService = translationService;
    this.searchContext = searchContext;

    const container = document.querySelector(
      ".horizontal-filter-content"
    ) as HTMLElement | null;
    if (!container) {
      console.warn(
        "ArticleListingPage: .horizontal-filter-content not found, skipping initialization"
      );
      return;
    }

    // Query pre-rendered placeholders; do not append programmatically
    this.dateRangeFilterSlot =
      container.querySelector<HTMLDivElement>(".date-range-slot");
    this.taxonomyFilterSlot =
      container.querySelector<HTMLDivElement>(".taxonomy-slot");

    if (!this.dateRangeFilterSlot || !this.taxonomyFilterSlot) {
      console.warn(
        'ArticleListingPage: required filter slots not found. Please add <div class="pwc-studio-js-search-filter date-range-slot"></div> and <div class="pwc-studio-js-search-filter taxonomy-slot"></div> inside .horizontal-filter-content.'
      );
      return;
    }

    this.service.addObserver(this.renderFilters.bind(this));
    this.initializeSearchContext();
    this.renderFilters();
  }

  private initializeSearchContext(): void {
    const container = document.querySelector(
      ".js-global-search-results-page"
    ) as HTMLElement | null;

    const ancestorIdAttr = container?.getAttribute("data-ancestor-id") ?? null;
    const docTypesAttr = container?.getAttribute("data-document-types") ?? null;
    const initialCategoriesAttr =
      container?.getAttribute("data-initial-categories") ?? "";
    const initialSortCode =
      container?.getAttribute("data-initial-sort") ?? "a0";

    // Validate ancestorId (must be positive integer if set)
    let ancestorId: string | null = null;
    if (ancestorIdAttr) {
      const parsed = parseInt(ancestorIdAttr, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        ancestorId = String(parsed);
      }
    }

    // Sanitize document types (safe alias pattern), cap count
    const safeAlias = (s: string) =>
      /^[A-Za-z0-9\-_.:]{1,100}$/.test(s.trim()) ? s.trim() : "";
    const documentTypes = (docTypesAttr?.split(",") ?? [])
      .map((x) => safeAlias(x))
      .filter((x) => x !== "")
      .slice(0, 100);

    // Server-side taxonomy subtree IDs (matches legacy GlobalSearch initial request)
    const categoryStrings = initialCategoriesAttr
      .split(",")
      .map((s) => s.trim())
      .filter((s) => /^\d{1,12}$/.test(s))
      .slice(0, 200);

    const sort = this.parseInitialSort(initialSortCode);

    this.service.setFilters({
      ...this.service.filters,
      ancestorId,
      documentTypes,
      categories: categoryStrings.length > 0 ? categoryStrings : [],
      orderBy: sort.orderBy,
      orderByAsc: sort.orderByAsc,
    });
  }

  private parseInitialSort(code: string): {
    orderBy: number;
    orderByAsc: boolean;
  } {
    switch (code.trim()) {
      case "a1":
        return { orderBy: 1, orderByAsc: true };
      case "d1":
        return { orderBy: 1, orderByAsc: false };
      case "a2":
        return { orderBy: 2, orderByAsc: true };
      case "d2":
        return { orderBy: 2, orderByAsc: false };
      case "a0":
      default:
        return { orderBy: 0, orderByAsc: true };
    }
  }

  // Veracode-recognized HTML encoder for UI text
  private vcEncodeHtml(value: string | undefined | null): string {
    const text = document.createTextNode(value ?? "");
    const p = document.createElement("p");
    p.appendChild(text);
    return p.innerHTML;
  }

  // Strict whitelist for facet values; 1..100 chars, limited safe set
  private sanitizeFacetValues(values: unknown): string[] {
    if (!Array.isArray(values)) return [];
    const out: string[] = [];
    const re = /^[A-Za-z0-9\-_.:]{1,100}$/;

    for (const v of values) {
      const t = String(v).trim();
      if (t.length === 0 || t.length > 100) continue;
      if (!re.test(t)) continue;
      out.push(t);
      if (out.length >= 100) break;
    }
    return out;
  }

  private renderFilters(): void {
    const container = document.querySelector(".horizontal-filter-content");
    const clearFilterButton = document.querySelector(
      ".horizontal-filter-clear-all"
    ) as HTMLButtonElement | null;

    if (!container || !this.dateRangeFilterSlot || !this.taxonomyFilterSlot) {
      console.error("Filter container or slots not found!");
      return;
    }

    // Bind once
    if (clearFilterButton && !clearFilterButton.dataset.bound) {
      clearFilterButton.addEventListener("click", (_) => this.clearFilters(), {
        passive: true,
      });
      clearFilterButton.dataset.bound = "1";
    }
    if (clearFilterButton) {
      if (!this.service.filtersSet()) {
        clearFilterButton.classList.add("hidden");
      } else {
        clearFilterButton.classList.remove("hidden");
      }
    }

    // Date range filter rendering into pre-existing slot
    if (!this.dateRangeFilter) {
      this.dateRangeFilter = new DateRangeSearchFilter(
        this.dateRangeFilterSlot,
        searchContext,
        (date: DateRange) => this.applyDateRangeFilter(date)
      );
    }

    // Taxonomy filter rendering into pre-existing slot
    const taxonomyDataRaw = this.service.context?.facets?.["taxonomy"] ?? [];
    const taxonomyData = this.sanitizeFacetValues(taxonomyDataRaw);

    const taxonomyfilterOptions = {
      filterLabel: this.vcEncodeHtml(this.apifilterMapping.taxonomy.label),
      placeholderLabel: this.vcEncodeHtml(
        this.apifilterMapping.taxonomy.placeholder
      ),
      data: taxonomyData,
    } as SearchFilterOptions;

    const onTaxonomyFilterApplied = (filters: string[]) => {
      const safeFilters = this.sanitizeFacetValues(filters);
      const globalSearchFilterInput = {
        ...this.service.filters,
        [this.apifilterMapping.taxonomy.filter]: safeFilters,
      };
      this.service.search(
        globalSearchFilterInput,
        1,
        null,
        null,
        languageService.getCurrentInShortFormat()
      );
    };

    if (!this.searchFilter) {
      this.searchFilter = new SearchFilter(
        this.taxonomyFilterSlot,
        this.translationService,
        taxonomyfilterOptions,
        onTaxonomyFilterApplied
      );
    } else {
      this.searchFilter.updateData(taxonomyfilterOptions.data);
    }
  }

  private applyDateRangeFilter(dateRange: DateRange) {
    globalSearchService.setFilters({
      ...globalSearchService.filters,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    });
    globalSearchService.refreshSearch();
  }

  private clearFilters() {
    this.dateRangeFilter?.clearFilter();
    this.searchFilter?.clearFilter();

    globalSearchService.resetFilters();
    globalSearchService.refreshSearch();
  }
}

export const initializeArticleListing = () => {
  const filterContainer = document.querySelector(".horizontal-filter-content");
  if (!filterContainer) {
    return;
  }

  new ArticleListingPage(
    globalSearchService,
    translationService,
    searchContext
  );

  const articleListing = document.querySelector(
    ".pwc-studio-article-listing"
  ) as HTMLElement | null;
  if (articleListing) {
    new ArticleListingCard(articleListing, globalSearchService);
  }

  if (
    globalSearchService.context.currentPage === 0 &&
    !globalSearchService.isLoading
  ) {
    globalSearchService.refreshSearch();
  }
};
